import Backdrop from 'components/backdrop';
import Map, { MapMarker, Tooltip } from 'components/map';
import { Button, Heading, Stack, Text } from 'components/ui';
import { FC, useState } from 'react';
import styled from 'styled-components';
import { breakpoints } from 'styles/theme';
import { getBoundsFromLocations } from 'utils/helpers';
import { formatAddress, formatAddressWeblink } from 'utils/sanity';
import { MapProps } from 'components/map';

interface LocationMapProps {
  locations?: Sanity.Schema.Location[];
}

const MapContainer = styled.div`
  --height: 500px;
`;

const Wrapper = styled(Backdrop)`
  padding-right: var(--space-1);
  padding-left: var(--space-1);

  @media screen and (min-width: ${breakpoints.small}) {
    padding-right: 0;
    padding-left: 0;
  }
`;

const Address = styled.address`
  font-style: normal;
  line-height: var(--line-height-x-large);
  white-space: pre-wrap;
`;

const LocationMap: FC<LocationMapProps> = ({ locations }) => {
  const [activeLocation, setActiveLocation] =
    useState<Sanity.Schema.Location>();

  const handleMarkerClick = (location: Sanity.Schema.Location) => {
    setActiveLocation(location);
  };

  const handleMapClick = () => {
    setActiveLocation(undefined);
  };

  const locationsWithoutPrimary = locations?.filter(
    (location) => !location.primary
  );

  // If there is one or less salon location (excluding primary, which is the main office)
  // center the map on the single location, or if there is none, default to the GPS coordinates [0,0]
  // If there is more than one salon location (excluding the primary main office)
  // Pass all salon coordinates to the fitBounds function which will make the map show all locations
  const mapProps: MapProps =
    locationsWithoutPrimary && locationsWithoutPrimary.length < 2
      ? {
          zoom: [14],
          center: [
            locationsWithoutPrimary[0].mapLocation?.lng || 0,
            locationsWithoutPrimary[0].mapLocation?.lat || 0,
          ],
        }
      : {
          fitBounds:
            locationsWithoutPrimary &&
            getBoundsFromLocations(locationsWithoutPrimary),
        };

  return (
    <Wrapper>
      <Stack space={{ initial: 2, large: 4, xLarge: 6 }}>
        <Heading level='h2'>Our locations</Heading>
        <MapContainer>
          <Map {...mapProps} onClick={handleMapClick}>
            <>
              {locationsWithoutPrimary?.map((location) => (
                <MapMarker
                  onClick={() => handleMarkerClick(location)}
                  key={location._id}
                  coordinates={[
                    location.mapLocation?.lng || 0,
                    location.mapLocation?.lat || 0,
                  ]}
                />
              ))}
              {activeLocation && (
                <Tooltip
                  coordinates={[
                    activeLocation?.mapLocation?.lng || 0,
                    activeLocation?.mapLocation?.lat || 0,
                  ]}
                >
                  <Stack space={1}>
                    <Text as='strong' fontWeight='bold'>
                      {activeLocation.name}
                    </Text>
                    <Address>{formatAddress(activeLocation)}</Address>
                    <Button
                      variant='outline'
                      as='a'
                      href={`https://www.google.com/maps/dir//${formatAddressWeblink(
                        activeLocation
                      )}`}
                    >
                      Get directions
                    </Button>
                  </Stack>
                </Tooltip>
              )}
            </>
          </Map>
        </MapContainer>
      </Stack>
    </Wrapper>
  );
};

export default LocationMap;
